import { DEFAULT_COORDINATES } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';
import { useState } from 'react';
import { getBraze } from '@utils/braze';

export const useCurrentLocation = ({ shouldUseDefaultCoordinates, onSuccess, onFail }) => {
  const { LocationStore } = useRootStore();
  const [errorMessage, setErrorMessage] = useState(null);

  const setDefaultCoordinates = () => {
    const defaultCoords = { latitude: DEFAULT_COORDINATES.LATITUDE, longitude: DEFAULT_COORDINATES.LONGITUDE };

    // Store in LocationStore
    LocationStore.setCurrentLocation(defaultCoords);

    if (onSuccess) onSuccess(defaultCoords);
  };

  const success = async position => {
    if (position.coords) {
      const { latitude, longitude } = position.coords;

      // Store in LocationStore for app-wide access
      LocationStore.setCurrentLocation({ latitude, longitude });

      if (onSuccess) onSuccess(position.coords);

      // Track location with Braze when successfully obtained
      try {
        const { braze } = await getBraze();
        braze.getUser().setLastKnownLocation(latitude, longitude);
        braze.getUser().setCustomUserAttribute('last_known_latitude', latitude);
        braze.getUser().setCustomUserAttribute('last_known_longitude', longitude);
      } catch (error) {
        // Silently fail if Braze is not available - don't block location functionality
        console.warn('Failed to track location with Braze:', error);
      }
    } else if (shouldUseDefaultCoordinates) {
      setDefaultCoordinates();
    }

    LocationStore.setIsLoading(false);
  };

  const error = () => {
    if (shouldUseDefaultCoordinates) {
      setErrorMessage('Location data was denied, using default location');
      setDefaultCoordinates();
    } else {
      setErrorMessage('Location data was denied, please check your browser settings');
    }
    if (onFail) {
      onFail();
    }
    LocationStore.setIsLoading(false);
  };

  const setCurrentLocation = () => {
    LocationStore.setIsLoading(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error);
    } else {
      setDefaultCoordinates();
      if (shouldUseDefaultCoordinates) {
        setErrorMessage('Geolocation is not supported by your browser');
      }
      LocationStore.setIsLoading(false);
    }
  };

  return { errorMessage, isLoading: LocationStore.isLoading, setCurrentLocation };
};
